'use client';

import { cn } from '@woonstad/helpers-utils';
import React, { useCallback, useMemo, useState } from 'react';
import { UseFormReset } from 'react-hook-form';

import { DirectionsChevronLeft, DirectionsChevronRight } from '../../../icons/directions';
import { IconButton } from '../../buttons/IconButton';
import { Spinner } from '../../spinner/Spinner';
import { Typography } from '../../typography/Typography';

export type Timeslot = {
  begin: string;
  beschikbaar: number;
  capaciteit: number;
  eind: string;
  id: string;
};

const getWeekDays = (currentDate: Date, maxPlannable?: number) => {
  const startOfWeek = new Date(currentDate.setDate(currentDate.getDate() - currentDate.getDay() + 1)); // Monday

  const daysToShow = maxPlannable && maxPlannable < 5 ? maxPlannable : 5;

  const weekDays = Array.from({
    length: daysToShow,
  }, (_, i) => {
    const day = new Date(startOfWeek);
    day.setDate(startOfWeek.getDate() + i);

    return day;
  });

  return weekDays;
};

const formatDateRange = (weekDays: Date[]) => {
  const formatDate = (date: Date) => date.toLocaleDateString('nl-NL', {
    day: 'numeric',
  });

  const formatDateWithMonth = (date: Date) => date.toLocaleDateString('nl-NL', {
    day: 'numeric',
    month: 'long',
  });

  const start = formatDate(weekDays[0]);
  const end = formatDateWithMonth(weekDays[weekDays.length - 1]);

  return `${start} - ${end}`;
};

const getDateAfterXDays = (startDate: Date, daysToAdd: number) => {
  const date = new Date(startDate);
  date.setDate(startDate.getDate() + daysToAdd);

  return date;
};

const dayNames = ['Ma', 'Di', 'Wo', 'Do', 'Vr'];

type Props = {
  // use this when there is a specific end date (that is not the last given timeslot date in the timeslots array).
  toDate?: Date;
  // use this if it is only for example possible to plan in the upcoming 25 days
  maxDaysPlannable?: number;
  // Show timeslots when you choose a date
  onClickDay?: (date: Date | undefined) => void;

  timeslots: Timeslot[];

  // Timeslots are still loading
  isLoading?: boolean;

  // Reset the form when a new date is clicked
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  formReset: UseFormReset<any>;
};

export const TimeslotPicker = ({
  onClickDay,
  toDate,
  maxDaysPlannable,
  timeslots,
  isLoading,
  formReset,
}: Props) => {
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [currentVisibleWeek, setCurrentVisibleWeek] = useState<number>(0);

  // Make sure that you see the date that has the first timeslot available
  const firstAvailableTimeslotDay = useMemo(() => {
    const availableTimeslotAfterCurrentDate = timeslots
      ? timeslots.find((timeslot) => timeslot.capaciteit > 1 && timeslot.beschikbaar > 1)
      : null;

    return availableTimeslotAfterCurrentDate ? new Date(availableTimeslotAfterCurrentDate.begin) : new Date();
  }, [timeslots]);

  // Calculate the weekdays for the current visible week based on the start date and current visible week.
  const weekDays = useMemo(() => {
    const copiedStartDate = new Date(firstAvailableTimeslotDay);

    return getWeekDays(
      new Date(copiedStartDate.setDate(copiedStartDate.getDate() + currentVisibleWeek * 7)),
      maxDaysPlannable,
    );
  }, [currentVisibleWeek, firstAvailableTimeslotDay, maxDaysPlannable]);

  const dateRange = useMemo(() => formatDateRange(weekDays), [weekDays]);

  const handleDateClick = useCallback((date: Date) => {
    setSelectedDate(date);
    if (onClickDay) onClickDay(date);
  }, [onClickDay]);

  // Show previous week
  const handlePreviousWeek = useCallback(() => {
    onClickDay?.(undefined);
    setSelectedDate(null);
    formReset();
    setCurrentVisibleWeek((curVal) => curVal - 1);
  }, [formReset, onClickDay]);

  // Show next week
  const handleNextWeek = useCallback(() => {
    onClickDay?.(undefined);
    setSelectedDate(null);
    formReset();
    setCurrentVisibleWeek((curVal) => curVal + 1);
  }, [formReset, onClickDay]);

  const isPreviousWeekDisabled = currentVisibleWeek === 0;

  const maxFutureDateBasedOnMaxDaysPlannable = useMemo(() => (
    maxDaysPlannable ? getDateAfterXDays(new Date(), maxDaysPlannable) : undefined
  ), [maxDaysPlannable]);

  // Check if the next week would exceed toDate or futureDate
  const isNextWeekDisabled = useMemo(() => (
    toDate || maxFutureDateBasedOnMaxDaysPlannable
      ? weekDays[weekDays.length - 1] >= (toDate ? toDate : maxFutureDateBasedOnMaxDaysPlannable ? maxFutureDateBasedOnMaxDaysPlannable : true)
      : false
  ), [maxFutureDateBasedOnMaxDaysPlannable, toDate, weekDays]);

  // Day is disabled when there is no timeslot
  const isDayDisabled = useCallback((date: Date) => {
    const dayTimeslots = timeslots?.filter((timeslot) => (
      new Date(timeslot.begin).toDateString() === date.toDateString() && timeslot.beschikbaar > 1 && timeslot.capaciteit > 1
    ));

    return dayTimeslots?.length === 0;
  }, [timeslots]);

  return (
    <div className="w-full">
      <div className="flex items-center justify-between pb-2">
        <IconButton
          icon={<DirectionsChevronLeft />}
          type="button"
          label="Vorige week"
          onClick={handlePreviousWeek}
          className={cn(isPreviousWeekDisabled ? 'pointer-events-none text-icon-button-disabled' : 'text-icon-button-enabled')}
          disabled={isPreviousWeekDisabled}
        />
        <Typography
          variant="label-regular"
          className="text-datepicker-title"
        >
          {dateRange}
        </Typography>
        <IconButton
          icon={<DirectionsChevronRight />}
          type="button"
          label="Volgende week"
          onClick={handleNextWeek}
          className={cn(isNextWeekDisabled ? 'pointer-events-none text-icon-button-disabled' : 'text-icon-button-enabled')}
          disabled={isNextWeekDisabled}
        />
      </div>

      {!isLoading ? (
        <div className="grid w-full grid-cols-5 gap-2">
          {weekDays.map((date, index) => {
            const isDisabled =
              isDayDisabled(date)
              || (maxFutureDateBasedOnMaxDaysPlannable && date > maxFutureDateBasedOnMaxDaysPlannable)
              || (toDate && date > toDate);

            const isSelected = selectedDate && date.toDateString() === selectedDate.toDateString();

            const hoverButtonClassName = 'hover:border-timeslotpicker-hover-enabled hover:bg-timeslotpicker-hover-enabled hover:text-timeslotpicker-hover-enabled hover:border-[3px]';

            const buttonClassName = isDisabled
              ? 'bg-timeslotpicker-disabled text-timeslotpicker-disabled'
              : isSelected
                ? 'box-border relative border-timeslotpicker-enabled bg-timeslotpicker-enabled text-timeslotpicker-enabled border-[3px]'
                : 'border-timeslotpicker-enabled bg-timeslotpicker-enabled text-timeslotpicker-enabled border';

            return (
              <button
                key={date.toDateString()}
                className={cn(
                  'flex h-16 w-full flex-col items-center justify-center rounded-lg p-2 text-center',
                  buttonClassName,
                  !isDisabled && hoverButtonClassName,
                )}
                type="button"
                disabled={isDisabled}
                onClick={() => handleDateClick(date)}
              >
                <Typography variant="content-body">{dayNames[index]}</Typography>
                <Typography variant="content-body">{date.getDate()}</Typography>
              </button>
            );
          })}
        </div>
      ) : (
        <div className="flex h-auto w-full flex-col items-center gap-2">
          <span
            className="size-5"
          >
            <Spinner />
          </span>
          <Typography
            variant="label-small"
            className="text-base-secondary-on-light"
          >
            Laden...
          </Typography>
        </div>
      )}
    </div>
  );
};

TimeslotPicker.displayName = 'TimeslotPicker';
